import type { PayloadAction, Slice } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { Payer, SplitOption } from '../../types/payer';
import {
  DamageClaim,
  InsurancePolicies,
  InsurancePolicy,
} from '@services/payersApi';
import { Customer } from '../../types/customer';
import { Location } from 'types/location';
import { Car, CarGroup, Equipment } from 'types/car';
import { InvoiceInfo } from 'types/invoiceInfo';
import { Price } from 'types/priceDetail';
import { StationOpeningHours } from '@pages/Reservation/reservation.slice';
import { Station, Stations } from '../../types/station';
import { StationsResponse } from '@services/stationsApi';

const now = DateTime.now().plus({ seconds: 5 }).toMillis();

export interface CheckoutStatePayload {
  number: string;
  prettyNumber: string;
  reservationId: string;
  reservationNumber: string;
  pickupDate: number;
  returnDate: number;
  comments: string;
  editable: boolean;
  deletable: boolean;
  onlineCheckin: boolean;
  onlineCheckinDone: boolean;
  key: string;
  duplicates: boolean;
  updateOnlineCheckin: boolean;
  car: Car;
  payer: Payer;
  invoiceInfo: InvoiceInfo;
  equipment: Equipment;
  customer: Customer;
  pickupLocation?: Location;
  returnLocation: Location;
  carGroup: CarGroup;
  carGroupCharged: CarGroup;
  price?: Price;
  hasReservedCar?: boolean;
  overrideDLCheck?: boolean;
  secondaryPayer: Payer;
  rentalCarInsurance: {
    cdw: boolean;
    tp: boolean;
    pai: boolean;
    scw: boolean;
  };
  confirmationCopyEmail?: string;
  confirmationCopyPhone?: string;
}

export interface CheckoutState {
  step: number;
  stations: Stations;
  reservation: {
    returnDate: number;
    pickupLocation: Location | null;
    returnLocation: Location | null;
    hasReservedCar: boolean;
    returnStationOpeningHours: StationOpeningHours;
  };
  invoiceInfo: InvoiceInfo;
  carGroupCharged: CarGroup | null;
  carGroups: CarGroup[] | null;
  selectedCar: Car | null;
  reservations: Record<string, any | null>[];
  activeReservation: Record<string, any> | null;
  isFinished: boolean;
  dayDifference: number;
  touched: Record<string, boolean>;
  errors: Record<string, boolean | string>;
  isValid: Record<string, boolean>;
  now: number;
  comment: string;
  customer: Customer;
  payer: Payer;
  payerList: Payer[];
  secondaryPayer: Payer;
  insurance_policies?: InsurancePolicies;
  selected_insurance_policy?: {
    insurance: InsurancePolicy;
    damages: DamageClaim[];
  };
  selected_damage_claim?: DamageClaim;
  shouldResetStateFromError?: boolean;
  isDriverLicenseControlRunning: boolean;
  confirmationCopyEmail: string | null;
  confirmationCopyPhone: string | null;
  confirmationCopyPhonePrefix: string | null;
  driverLicenseCheckErrorMessage: string;
  birthDay: string | number;
  birthMonth: string | number;
  birthYear: string | number;
  showInsuranceSearch: boolean;
}

export const initialState: CheckoutState = {
  step: 0,
  stations: null,
  reservation: {
    returnDate: now,
    pickupLocation: {
      station: null,
      address: null,
      pickupPlace: null,
    },
    returnLocation: {
      station: null,
      address: null,
      pickupPlace: null,
    },
    hasReservedCar: false,
    returnStationOpeningHours: null,
  },
  invoiceInfo: {
    invoiceRecipient: '',
    customerCarLicenseNumber: '',
    damageNumber: '',
    invoiceReference: '',
    invoiceText: '',
  },
  reservations: [],
  carGroupCharged: null,
  carGroups: null,
  selectedCar: null,
  activeReservation: null,
  isFinished: false,
  dayDifference: 1,
  touched: {
    activeReservation: false,
  },
  errors: {
    returnDate: true,
  },
  isValid: {},
  now: now,
  comment: '',
  customer: {
    firstName: '',
    lastName: '',
    licenseNumber: '',
    phone: '',
    email: '',
    city: '',
    address: '',
    zip: '',
    birthDate: null,
    phoneIsoCode: '',
    country: '',
    hasEmail: null,
    phonePrefix: '',
    bisnodeId1: '',
    bisnodeSearchResultIsNull: null,
    bisnodePhoneNumber: '',
    bisnodePhoneIsoCode: '',
    bisnodePhonePrefix: '',
  },
  payer: null,
  payerList: [],
  secondaryPayer: null,
  insurance_policies: null,
  selected_insurance_policy: null,
  selected_damage_claim: null,
  shouldResetStateFromError: true,
  isDriverLicenseControlRunning: false,
  confirmationCopyEmail: null,
  confirmationCopyPhone: null,
  confirmationCopyPhonePrefix: null,
  driverLicenseCheckErrorMessage: '',
  birthDay: '',
  birthMonth: '',
  birthYear: '',
  showInsuranceSearch: false,
};

const checkoutSlice: Slice<CheckoutState> = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    setShowInsuranceSearch: (state, action: PayloadAction<boolean>) => {
      state.showInsuranceSearch = action.payload;
    },
    setBirthDay: (state, action: PayloadAction<string | number>) => {
      state.birthDay = action.payload;
    },
    setBirthMonth: (state, action: PayloadAction<string | number>) => {
      state.birthMonth = action.payload;
    },
    setBirthYear: (state, action: PayloadAction<string | number>) => {
      state.birthYear = action.payload;
    },
    setStep: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
    setStations: (state, action: PayloadAction<StationsResponse>) => {
      state.stations = action.payload;
    },
    setConfirmationCopyEmail: (state, action: PayloadAction<string>) => {
      state.confirmationCopyEmail = action.payload;
    },
    setConfirmationCopyPhone: (state, action: PayloadAction<string>) => {
      state.confirmationCopyPhone = action.payload;
    },
    setConfirmationCopyPhonePrefix: (state, action: PayloadAction<string>) => {
      state.confirmationCopyPhonePrefix = action.payload;
    },
    setDriverLicenseCheckErrorMessage: (
      state,
      action: PayloadAction<string>
    ) => {
      state.driverLicenseCheckErrorMessage = action.payload;
    },
    setIsDriverLicenseControlRunning: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isDriverLicenseControlRunning = action.payload;
    },
    setCarGroupCharged: (state, action: PayloadAction<CarGroup>) => {
      state.carGroupCharged = action.payload;
    },
    setReturnStationOpeningHours: (
      state,
      action: PayloadAction<StationOpeningHours>
    ) => {
      state.reservation.returnStationOpeningHours = {
        ...state.reservation.returnStationOpeningHours,
        ...action.payload,
      };
    },
    setSelectedVoucherSplitOption: (
      state,
      action: PayloadAction<SplitOption>
    ) => {
      state.payer.selectedVoucherSplitOption = action.payload;
    },
    setCarGroups: (state, action: PayloadAction<CarGroup[]>) => {
      state.carGroups = action.payload;
    },
    setSelectedCar: (state, action: PayloadAction<Car>) => {
      state.selectedCar = action.payload;
    },
    setReturnDate: (state, action: PayloadAction<number>) => {
      state.reservation.returnDate = action.payload;
    },
    setDayDifference: (state, action: PayloadAction<number>) => {
      state.dayDifference = action.payload;
    },
    setReservations: (state, action: PayloadAction<Record<string, any>[]>) => {
      state.reservations = action.payload;
    },
    setPickupLocation: (state, action: PayloadAction<Location>) => {
      state.reservation.pickupLocation = action.payload;
    },
    setPickupStation: (state, action: PayloadAction<Station>) => {
      state.reservation.pickupLocation.station = action.payload;
    },
    setPickupPlace: (
      state,
      action: PayloadAction<{ id: string; name: string }>
    ) => {
      state.reservation.pickupLocation.pickupPlace = action.payload;
    },
    setReturnLocation: (state, action: PayloadAction<Location>) => {
      state.reservation.returnLocation = action.payload;
    },
    setReturnStation: (state, action: PayloadAction<Station>) => {
      state.reservation.returnLocation.station = action.payload;
    },
    setReturnPlace: (
      state,
      action: PayloadAction<{ id: string; name: string }>
    ) => {
      state.reservation.returnLocation.pickupPlace = action.payload;
    },
    setHasReservedCar: (state, action: PayloadAction<boolean>) => {
      state.reservation.hasReservedCar = action.payload;
    },
    setActiveReservation: (
      state,
      action: PayloadAction<Record<string, any>>
    ) => {
      state.activeReservation = action.payload;
    },
    setIsFinished: (state, action: PayloadAction<boolean>) => {
      state.isFinished = action.payload;
    },
    setTouched: (state, action: PayloadAction<Record<string, boolean>>) => {
      state.touched = { ...state.touched, ...action.payload };
    },
    setErrors: (
      state,
      action: PayloadAction<Record<string, boolean | string>>
    ) => {
      state.errors = { ...state.errors, ...action.payload };
    },
    setIsValid: (state, action: PayloadAction<Record<string, boolean>>) => {
      state.isValid = { ...state.isValid, ...action.payload };
    },
    setComment: (state, action: PayloadAction<string>) => {
      state.comment = action.payload;
    },
    setInvoiceRecipient: (state, action: PayloadAction<string>) => {
      state.invoiceInfo.invoiceRecipient = action.payload;
    },
    setDamageNumber: (state, action: PayloadAction<string>) => {
      state.invoiceInfo.damageNumber = action.payload;
    },
    setCarLicenseNumber: (state, action: PayloadAction<string>) => {
      state.invoiceInfo.customerCarLicenseNumber = action.payload;
    },
    setInvoiceRef: (state, action: PayloadAction<string>) => {
      state.invoiceInfo.invoiceReference = action.payload;
    },
    setInvoiceText: (state, action: PayloadAction<string>) => {
      state.invoiceInfo.invoiceText = action.payload;
    },
    setCustomer: (state, action: PayloadAction<Customer>) => {
      state.customer = action.payload;
    },
    setCustomerBirthDate: (state, action: PayloadAction<string>) => {
      state.customer.birthDate = action.payload;
    },
    setCustomerFirstName: (state, action: PayloadAction<string>) => {
      state.customer.firstName = action.payload;
    },
    setCustomerLastName: (state, action: PayloadAction<string>) => {
      state.customer.lastName = action.payload;
    },
    setCustomerDriverLicenseNumber: (state, action: PayloadAction<string>) => {
      state.customer.licenseNumber = action.payload;
    },
    setCustomerPhonePrefix: (state, action: PayloadAction<string>) => {
      state.customer.phonePrefix = action.payload;
    },
    setCustomerPhoneIsoCode: (state, action: PayloadAction<string>) => {
      state.customer.phoneIsoCode = action.payload;
    },
    setCustomerPhoneNumber: (state, action: PayloadAction<string>) => {
      state.customer.phone = action.payload;
    },
    setHasEmail: (state, action: PayloadAction<boolean>) => {
      state.customer.hasEmail = action.payload;
    },
    setCustomerEmail: (state, action: PayloadAction<string>) => {
      state.customer.email = action.payload;
    },
    setCustomerCountry: (state, action: PayloadAction<string>) => {
      state.customer.country = action.payload;
    },
    setCustomerAddress: (state, action: PayloadAction<string>) => {
      state.customer.address = action.payload;
    },
    setCustomerZip: (state, action: PayloadAction<string>) => {
      state.customer.zip = action.payload;
    },
    setCustomerCity: (state, action: PayloadAction<string>) => {
      state.customer.city = action.payload;
    },
    setPayer: (state, action: PayloadAction<Payer>) => {
      state.payer = action.payload;
    },
    setPayerList: (state, action: PayloadAction<Payer[]>) => {
      state.payerList = action.payload;
    },
    setCcData: (state, action: PayloadAction<Payer['ccData']>) => {
      state.payer.ccData = action.payload;
    },
    setSecondaryPayer: (state, action: PayloadAction<Payer>) => {
      state.secondaryPayer = action.payload;
    },
    setInsurancePolicies: (state, action: PayloadAction<InsurancePolicies>) => {
      state.insurance_policies = action.payload;
    },
    setSelectedInsurancePolicy: (
      state,
      action: PayloadAction<{
        insurance: InsurancePolicy;
        damages: DamageClaim[];
      }>
    ) => {
      state.selected_insurance_policy = action.payload;
    },
    setSelectedDamageClaim: (state, action: PayloadAction<DamageClaim>) => {
      state.selected_damage_claim = action.payload;
    },
    setBisnodeId1: (state, action: PayloadAction<string>) => {
      state.customer.bisnodeId1 = action.payload;
    },
    setBisnodeSearchResultIsNull: (state, action: PayloadAction<boolean>) => {
      state.customer.bisnodeSearchResultIsNull = action.payload;
    },
    setBisnodePhoneNumber: (state, action: PayloadAction<string>) => {
      state.customer.bisnodePhoneNumber = action.payload;
    },
    setBisnodePhoneIsoCode: (state, action: PayloadAction<string>) => {
      state.customer.bisnodePhoneIsoCode = action.payload;
    },
    setBisnodePhonePrefix: (state, action: PayloadAction<string>) => {
      state.customer.bisnodePhonePrefix = action.payload;
    },
    setShouldResetStateFromError: (state, action: PayloadAction<boolean>) => {
      state.shouldResetStateFromError = action.payload;
    },
    resetCheckout: (state) => {
      return {
        ...initialState,
        reservations: state.reservations,
      };
    },
  },
});

export const {
  setBirthDay,
  setBirthMonth,
  setBirthYear,
  setStep,
  setStations,
  setSelectedVoucherSplitOption,
  setReturnDate,
  setSelectedCar,
  setCarGroups,
  setDayDifference,
  setActiveReservation,
  setReservations,
  setPickupPlace,
  setReturnPlace,
  setHasReservedCar,
  setIsFinished,
  setTouched,
  setErrors,
  setIsValid,
  resetCheckout,
  setComment,
  setDamageNumber,
  setCarLicenseNumber,
  setInvoiceRef,
  setInvoiceText,
  setCustomer,
  setPayer,
  setPayerList,
  setCcData,
  setPickupStation,
  setReturnStation,
  setSecondaryPayer,
  setSelectedInsurancePolicy,
  setInsurancePolicies,
  setSelectedDamageClaim,
  setCustomerFirstName,
  setCustomerLastName,
  setCustomerDriverLicenseNumber,
  setCustomerPhonePrefix,
  setConfirmationCopyEmail,
  setConfirmationCopyPhone,
  setConfirmationCopyPhonePrefix,
  setCustomerPhoneIsoCode,
  setCustomerPhoneNumber,
  setHasEmail,
  setCustomerEmail,
  setCustomerBirthDate,
  setCustomerCountry,
  setCustomerAddress,
  setCustomerZip,
  setCustomerCity,
  setBisnodeId1,
  setBisnodeSearchResultIsNull,
  setBisnodePhoneNumber,
  setBisnodePhoneIsoCode,
  setBisnodePhonePrefix,
  setPickupLocation,
  setReturnLocation,
  setCarGroupCharged,
  setShouldResetStateFromError,
  setIsDriverLicenseControlRunning,
  setReturnStationOpeningHours,
  setDriverLicenseCheckErrorMessage,
  setInvoiceRecipient,
  setShowInsuranceSearch,
} = checkoutSlice.actions;
export default checkoutSlice.reducer;
